import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';

// layout 
import AppLayout from '../../../layouts/AppLayout/AppLayout';

// components 
import { NioSection, NioField, NioIcon, NioBadge, NioButton, NioCard, NioToaster } from '../../../components';

// API
import api from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';

function UserRequest() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const serviceGroups = {
    all: t('home.groups.all'),
    'Digital & Professional': ['Digital', 'Professional'],
    'Personal & Family': ['Personal', 'Family'],
    'Home Services': ['Home', 'Service', 'Maintenance', 'Repair'],
    'Moving & Delivery': ['Moving', 'Delivery'],
    'Gardening & Outdoor': ['Gardening', 'Outdoor'],
  };
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedSubService, setSelectedSubService] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [notes, setNotes] = useState('');
  const getServicePriority = (service) => {
    const t = String(service?.title || '').toLowerCase();
    if (t.includes('sol') || t.includes('mur')) return 0;
    if (t.includes('meuble') || t.includes('décoration') || t.includes('decoration')) return 1;
    if (t.includes('cuisine')) return 2;
    if (t.includes('salle de bain') || t.includes('salledebain') || t.includes('bain')) return 3;
    if (t.includes('plomberie') || t.includes('chauffage')) return 4;
    if (t.includes('menuiserie') || t.includes('bois')) return 5;
    if (t.includes('jardinage') || t.includes('jardin') || t.includes('extérieur') || t.includes('exterieur')) return 6;
    if (t.includes('électricité') || t.includes('electricite') || t.includes('électrique') || t.includes('electrique')) return 7;
    if (t.includes('déménagement') || t.includes('demenagement') || t.includes('transport')) return 8;
    if (t.includes('ménage') || t.includes('menage') || t.includes('nettoyage') || t.includes('netoyage')) return 9;
    if (t.includes('sécurité') || t.includes('securite') || t.includes('serrurerie')) return 10;
    return 99;
  };

  // Fetch services from backend
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Auto-init non-destructif : ajoute seulement les services manquants (dédup = admin)
        try {
          await api.initializeServices();
        } catch (error) {
          console.log('Services init:', error.message || 'skipped');
        }
        
        // Try to initialize services if empty
        await api.initializeServices(false);
        
        // Fetch services
        const response = await api.getServices({});
        
        if (response.success) {
          setServices(response.data || []);
        }
      } catch (err) {
        console.error('Error fetching services:', err);
        setError(err.message || t('userRequest.loadError'));
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Filter services based on search/group/sort (same style as Home)
  const filteredServices = useMemo(() => {
    let filtered = [...services];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((service) =>
        service.title?.toLowerCase().includes(query) ||
        service.description?.toLowerCase().includes(query) ||
        (Array.isArray(service.tags) ? service.tags.some((tag) => String(tag).toLowerCase().includes(query)) : false)
      );
    }

    if (selectedGroup !== 'all') {
      const groupTags = serviceGroups[selectedGroup] || [];
      filtered = filtered.filter((service) => {
        const tags = Array.isArray(service.tags) ? service.tags : [service.tags];
        return tags.some((tag) => groupTags.includes(tag));
      });
    }

    if (sortBy === 'name-asc') {
      filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else if (sortBy === 'name-desc') {
      filtered.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
    } else if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else {
      // Keep a stable visual order close to the reference grid.
      filtered.sort((a, b) => {
        const priorityA = getServicePriority(a);
        const priorityB = getServicePriority(b);
        if (priorityA !== priorityB) return priorityA - priorityB;
        return (a.title || '').localeCompare(b.title || '');
      });
    }

    return filtered;
  }, [services, searchQuery, selectedGroup, sortBy]);
  const featuredServices = filteredServices.slice(0, 2);
  const otherServices = filteredServices.slice(2);

  const availableSubServices = useMemo(() => {
    const subs = [];
    selectedServices.forEach((service) => {
      if (Array.isArray(service.subServices)) {
        service.subServices.forEach((sub) => {
          if (!subs.some((s) => s.key === sub.key)) subs.push(sub);
        });
      }
    });
    return subs;
  }, [selectedServices]);

  const handleServiceSelect = (service) => {
    setSelectedServices(prev => {
      const isSelected = prev.some(s => s._id === service._id || s.title === service.title);
      if (isSelected) {
        return prev.filter(s => s._id !== service._id && s.title !== service.title);
      } else {
        return [...prev, service];
      }
    });
  };

  const isServiceSelected = (service) => {
    return selectedServices.some(s => s._id === service._id || s.title === service.title);
  };
  const isEmphasizedService = (service) => {
    const title = String(service?.title || '').toLowerCase();
    return title.includes('mécan') ||
      title.includes('mecan') ||
      title.includes('sécurité') ||
      title.includes('securite') ||
      title.includes('serrurerie');
  };
  const isMechanicService = (service) => {
    const title = String(service?.title || '').toLowerCase();
    return title.includes('mécan') || title.includes('mecan');
  };
  const getServiceSubServicesRoute = (service) => {
    if (service?.slug) return `/services/${service.slug}`;
    return null;
  };
  const getServiceCover = (service) => {
    if (isMechanicService(service)) return '/images/mecanique-aa.png';
    const cover = service.cover || '/images/blog/a.jpg';
    return cover.startsWith('/') ? cover : `/${cover}`;
  };

  const handleSubmitRequest = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (selectedServices.length === 0) {
      setError(t('userRequest.selectOneService'));
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    if (!userEmail) {
      setError(t('userRequest.enterEmail'));
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      const requestData = {
        userEmail,
        userName: userName || undefined,
        userPhone: userPhone || undefined,
        services: selectedServices.map(service => ({
          serviceId: service._id,
          title: service.title,
          tags: Array.isArray(service.tags) ? service.tags : [service.tags].filter(Boolean)
        })),
        subService: selectedSubService || undefined,
        notes: notes || undefined
      };

      const response = await api.submitRequest(requestData);

      if (response.success) {
        setShowSuccess(true);
        // Reset form
        setSelectedServices([]);
        setSelectedSubService('');
        setUserEmail('');
        setUserName('');
        setUserPhone('');
        setNotes('');
        setTimeout(() => {
          navigate('/services');
        }, 2000);
      } else {
        setError(response.message || t('userRequest.submitError'));
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      }
    } catch (err) {
      console.error('Submit request error:', err);
      setError(err.message || t('userRequest.submitError'));
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  const toasterMessages = [];
  if (showError) {
    toasterMessages.push({ id: 'error', type: 'error', message: error });
  }
  if (showSuccess) {
    toasterMessages.push({ id: 'success', type: 'success', message: t('userRequest.submitSuccess') });
  }

  return (
  

    <AppLayout title={t('userRequest.pageTitle')} rootClass="layout-1">
      <NioToaster messages={toasterMessages} />
      <style>{`
        .services-filter-wrap {
          background: #f8fbff;
          border: 1px solid #e9eff6;
          border-radius: 16px;
          padding: 1.25rem;
        }
        @media (min-width: 768px) {
          .services-filter-wrap {
            padding: 1.5rem;
          }
        }
        .services-grid-tile {
          position: relative;
          display: block;
          overflow: hidden;
          border-radius: 16px;
          min-height: 190px;
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
          transition: box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease;
        }
        .services-grid-tile:hover {
          box-shadow: 0 14px 36px rgba(15, 23, 42, 0.2);
          transform: translateY(-3px);
        }
        .services-grid-tile--large {
          min-height: 235px;
        }
        @media (min-width: 992px) {
          .services-grid-tile--large {
            min-height: 255px;
          }
        }
        .services-grid-tile__bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          transition: transform 0.45s ease;
        }
        .services-grid-tile:hover .services-grid-tile__bg {
          transform: scale(1.06);
        }
        .services-grid-tile__overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            rgba(15, 23, 42, 0.25) 0%,
            rgba(15, 23, 42, 0.55) 100%
          );
        }
        .services-grid-tile__badge {
          position: absolute;
          top: 0;
          right: 0;
          z-index: 2;
        }
        .services-grid-tile__title {
          position: absolute;
          inset: 0;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          margin: 0;
          color: #fff;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          font-weight: 700;
          text-align: center;
          text-shadow: 0 2px 14px rgba(0, 0, 0, 0.85);
          font-size: clamp(0.75rem, 1.5vw, 0.95rem);
          line-height: 1.3;
        }
        .services-grid-tile--large .services-grid-tile__title {
          font-size: clamp(0.9rem, 2vw, 1.2rem);
          letter-spacing: 0.08em;
        }
      `}</style>

      <NioSection className="nk-section-services pt-6 pt-lg-7 pb-2 overflow-hidden" masks={["blur-1 left center"]}>
        <NioSection.Content>
          <Row className="justify-content-center text-center">
            <Col lg={9} xl={7}>
              <div className="nk-section-head">
                <span className="d-inline-block fs-14 text-uppercase text-primary fw-semibold mb-2">{t('userRequest.pageTitle')}</span>
                <h2 className="h1 fw-bold mb-3" style={{ letterSpacing: '-0.02em' }}>{t('userRequest.pageTitle')}</h2>
                <p className="fs-20 text-muted mb-0">
                  {t('userRequest.pageSubtitle')}
                </p>
              </div>
            </Col>
            <Col lg={10} xl={12}>
              <div className="nk-filter-wrap pb-5 pb-md-7 services-filter-wrap">
                <div className="mb-4">
                  <NioField.Input
                    icon="search before z-1"
                    placeholder={t('home.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Row className="g-3 mb-4">
                  <Col md={6} lg={4}>
                    <div className="form-group text-start">
                      <label className="form-label fw-semibold mb-2">{t('home.groupLabel')}</label>
                      <select
                        className="form-control form-control-lg"
                        value={selectedGroup}
                        onChange={(e) => setSelectedGroup(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      >
                        {Object.keys(serviceGroups).map((group) => (
                          <option key={group} value={group}>
                            {Array.isArray(serviceGroups[group]) ? group : serviceGroups[group]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Col>
                  <Col md={6} lg={4}>
                    <div className="form-group text-start">
                      <label className="form-label fw-semibold mb-2">{t('home.sortLabel')}</label>
                      <select
                        className="form-control form-control-lg"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      >
                        <option value="default">{t('home.sortDefault')}</option>
                        <option value="name-asc">{t('home.sortNameAsc')}</option>
                        <option value="name-desc">{t('home.sortNameDesc')}</option>
                        <option value="newest">{t('home.sortNewest')}</option>
                      </select>
                    </div>
                  </Col>
                  <Col md={12} lg={4}>
                    <div className="form-group text-start">
                      <label className="form-label fw-semibold mb-2">
                        {t('home.results', { count: filteredServices.length })}
                      </label>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => {
                            setSearchQuery('');
                            setSelectedGroup('all');
                            setSortBy('default');
                          }}
                        >
                          {t('home.clearFilters')}
                        </button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          {/* Services List Section */}
          <Row className="justify-content-center mb-5">
            <Col xl={12}>
              <div className="mb-4">
                <h4 className="text-center mb-4">{t('userRequest.availableServices')}</h4>
                <p className="text-center text-muted mb-4">{t('userRequest.selectHint')}</p>
              </div>
              {loading ? (
                <div className="text-center py-5">
                  <p className="fs-18">{t('home.loadingServices')}</p>
                </div>
              ) : filteredServices.length > 0 ? (
                <>
                  {featuredServices.length > 0 && (
                    <Row className="g-3 g-lg-4 mb-3 mb-lg-4">
                      {featuredServices.map((service, idx) => {
                        const isSelected = isServiceSelected(service);
                        const cover = getServiceCover(service);
                        return (
                          <Col xs={12} md={featuredServices.length === 1 ? 12 : 6} key={service._id || `f-${idx}`}>
                            <div
                              className={`services-grid-tile services-grid-tile--large ${isSelected ? 'border border-primary border-2' : ''}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                handleServiceSelect(service);
                                const route = getServiceSubServicesRoute(service);
                                if (route) navigate(route);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleServiceSelect(service);
                                  const route = getServiceSubServicesRoute(service);
                                  if (route) navigate(route);
                                }
                              }}
                            >
                              <div
                                className="services-grid-tile__bg"
                                style={{ backgroundImage: `url(${cover})` }}
                              />
                              <div className="services-grid-tile__overlay" aria-hidden />
                              <span className="services-grid-tile__title">{service.titles?.[language] || service.title}</span>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                  {otherServices.length > 0 && (
                    <Row className="g-3 g-lg-4">
                      {otherServices.map((service, idx) => {
                        const isSelected = isServiceSelected(service);
                        const isEmphasized = isEmphasizedService(service);
                        const cover = getServiceCover(service);
                        return (
                          <Col
                            xs={6}
                            md={isEmphasized ? 12 : 6}
                            lg={isEmphasized ? 6 : 3}
                            key={service._id || `o-${idx}`}
                          >
                            <div
                              className={`services-grid-tile ${isEmphasized ? 'services-grid-tile--large' : ''} ${isSelected ? 'border border-primary border-2' : ''}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                handleServiceSelect(service);
                                const route = getServiceSubServicesRoute(service);
                                if (route) navigate(route);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleServiceSelect(service);
                                  const route = getServiceSubServicesRoute(service);
                                  if (route) navigate(route);
                                }
                              }}
                            >
                              <div
                                className="services-grid-tile__bg"
                                style={{ backgroundImage: `url(${cover})` }}
                              />
                              <div className="services-grid-tile__overlay" aria-hidden />
                              <span className="services-grid-tile__title">{service.titles?.[language] || service.title}</span>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <p className="fs-18">{t('home.noServices')}</p>
                </div>
              )}
            </Col>
          </Row>

          {/* Selected Services Summary */}
          {selectedServices.length > 0 && (
            <Row className="justify-content-center mb-5">
              <Col lg={10}>
                <NioCard className="bg-primary-soft border-primary">
                  <NioCard.Body>
                    <div className="mb-4">
                      <h5 className="mb-3">{t('userRequest.selectedTitle')} ({selectedServices.length})</h5>
                      <div className="d-flex flex-wrap gap-2 mb-4">
                        {selectedServices.map((service, idx) => (
                          <NioBadge 
                            key={idx}
                            rounded 
                            className="text-bg-primary"
                            label={service.titles?.[language] || service.title}
                          />
                        ))}
                      </div>
                    </div>
                    {availableSubServices.length > 0 && (
                      <div className="mb-4 text-start">
                        <label className="form-label fw-semibold mb-2">{t('postRequest.subService')}</label>
                        <select
                          className="form-control form-control-lg"
                          value={selectedSubService}
                          onChange={(e) => setSelectedSubService(e.target.value)}
                          style={{ cursor: 'pointer' }}
                        >
                          <option value="">{t('postRequest.selectSubService')}</option>
                          {availableSubServices.map((sub) => (
                            <option key={sub.key} value={sub.key}>{sub.labels?.fr || sub.labels?.en || sub.key}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </NioCard.Body>
                </NioCard>
              </Col>
            </Row>

         
)}</NioSection.Content>
        </NioSection>
      </AppLayout>
    );
  }

export default UserRequest;
